/**
 * Cliente del API de reporte de consumo de IA.
 * Backend: app/api/v1/usage.py · Doc 25 §6.1.
 *
 * Endpoints:
 *   GET /usage/summary    — KPIs del periodo (admin ve balance).
 *   GET /usage/breakdown  — agregado por eje (model/agent/role/...).
 *   GET /usage/records    — ledger granular paginado por cursor.
 *   GET /usage/balance    — saldo prepago + historial (admin only).
 *
 * Política BYOK (Doc 25 D2): `cost_provider_usd` y `cost_billed_usd`
 * llegan `null` en filas con `byok=true`. El frontend muestra "BYOK"
 * en lugar de costo.
 */

import { apiFetch } from './client'

// ─── Summary ─────────────────────────────────────────────────────────────────
export interface UsageTokens {
  input: number
  output: number
  cached_input: number
  reasoning: number
  total: number
}

export interface UsageCost {
  /** Costo real pagado a providers (Azure/OpenAI/Anthropic). */
  provider_usd: string
  /** Costo facturado al tenant (provider * (1 + markup_pct/100)). */
  billed_usd: string
}

export interface UsageByokShare {
  calls: number
  input_tokens: number
  output_tokens: number
}

export interface UsageSummary {
  period_from: string
  period_to: string
  tokens: UsageTokens
  cost: UsageCost
  byok_share: UsageByokShare
  calls: number
  /** Solo populado para tenant admins. */
  balance_usd: string | null
  low_balance_threshold_usd: string | null
}

// ─── Breakdown ───────────────────────────────────────────────────────────────
export type UsageGroupBy =
  | 'model'
  | 'agent'
  | 'role'
  | 'project'
  | 'day'
  | 'user'

export interface UsageBreakdownRow {
  /** Null cuando la FK fue limpiada por ON DELETE SET NULL. */
  group_key: string | null
  /** Label human-readable. Backend lo popula para `user` (display_name)
   *  y `project` (`code · name`). Para agents/roles el frontend traduce
   *  por i18n; para `day` formatea con locale del usuario. */
  group_label?: string | null
  calls: number
  input_tokens: number
  output_tokens: number
  cached_input_tokens: number
  reasoning_tokens: number
  cost_provider_usd: string
  cost_billed_usd: string
  byok_calls: number
}

export interface UsageBreakdown {
  group_by: UsageGroupBy
  period_from: string
  period_to: string
  rows: UsageBreakdownRow[]
}

// ─── Records (ledger) ───────────────────────────────────────────────────────
export interface UsageRecord {
  id: string
  created_at: string
  project_id: string | null
  artifact_id: string | null
  user_id: string | null
  agent_name: string
  role: string
  model_id: string
  provider: string
  input_tokens: number
  output_tokens: number
  reasoning_tokens: number | null
  cached_input_tokens: number | null
  /** Null para rows BYOK (política Doc 25 D2). */
  cost_provider_usd: string | null
  cost_billed_usd: string | null
  byok: boolean
  latency_ms: number | null
  request_id: string | null
  finish_reason: string | null
}

export interface UsageRecordsPage {
  items: UsageRecord[]
  /** Opaco. Pasar como `?cursor=` en la siguiente petición. */
  next_cursor: string | null
}

// ─── Balance ─────────────────────────────────────────────────────────────────
export interface TenantTopup {
  id: string
  amount_usd: string
  payment_method: string
  payment_ref: string | null
  notes: string | null
  created_by_user_id: string | null
  created_at: string
}

export interface UsageBalance {
  balance_usd: string
  low_balance_threshold_usd: string | null
  total_consumed_usd: string
  last_topup_at: string | null
  last_consumption_at: string | null
  block_when_zero: boolean
  topups: TenantTopup[]
}

// ─── Filtros comunes ─────────────────────────────────────────────────────────
export interface UsageFilters {
  from?: string
  to?: string
  projectId?: string
}

export interface UsageRecordsFilters extends UsageFilters {
  agentName?: string
  modelId?: string
  role?: string
  byok?: boolean
  /** Filtra por `llm_usage_records.request_id` — útil para el modal
   * de comments que correlaciona una invocación específica. */
  requestId?: string
  cursor?: string
  limit?: number
}

function _qs(params: Record<string, string | number | boolean | undefined>): string {
  const entries = Object.entries(params).filter(
    ([, v]) => v !== undefined && v !== null && v !== '',
  )
  if (entries.length === 0) return ''
  const sp = new URLSearchParams()
  for (const [k, v] of entries) sp.set(k, String(v))
  return `?${sp.toString()}`
}

// ─── Fetchers ────────────────────────────────────────────────────────────────
export async function getUsageSummary(
  accessToken: string,
  filters: UsageFilters = {},
  options: { activeTenantId?: string } = {},
): Promise<UsageSummary> {
  const qs = _qs({
    from: filters.from,
    to: filters.to,
    project_id: filters.projectId,
  })
  return apiFetch<UsageSummary>(`/api/v1/usage/summary${qs}`, {
    accessToken,
    activeTenantId: options.activeTenantId,
  })
}

export async function getUsageBreakdown(
  accessToken: string,
  groupBy: UsageGroupBy,
  filters: UsageFilters = {},
  options: { activeTenantId?: string } = {},
): Promise<UsageBreakdown> {
  const qs = _qs({
    group_by: groupBy,
    from: filters.from,
    to: filters.to,
    project_id: filters.projectId,
  })
  return apiFetch<UsageBreakdown>(`/api/v1/usage/breakdown${qs}`, {
    accessToken,
    activeTenantId: options.activeTenantId,
  })
}

export async function getUsageRecords(
  accessToken: string,
  filters: UsageRecordsFilters = {},
  options: { activeTenantId?: string } = {},
): Promise<UsageRecordsPage> {
  const qs = _qs({
    from: filters.from,
    to: filters.to,
    project_id: filters.projectId,
    agent_name: filters.agentName,
    model_id: filters.modelId,
    role: filters.role,
    byok: filters.byok,
    request_id: filters.requestId,
    cursor: filters.cursor,
    limit: filters.limit,
  })
  return apiFetch<UsageRecordsPage>(`/api/v1/usage/records${qs}`, {
    accessToken,
    activeTenantId: options.activeTenantId,
  })
}

export async function getUsageBalance(
  accessToken: string,
  options: { activeTenantId?: string; topupsLimit?: number } = {},
): Promise<UsageBalance> {
  const qs = _qs({ topups_limit: options.topupsLimit })
  return apiFetch<UsageBalance>(`/api/v1/usage/balance${qs}`, {
    accessToken,
    activeTenantId: options.activeTenantId,
  })
}
